import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import TeamMap from "@/components/TeamMap";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Clock, Timer, Users, UserCheck, CalendarOff, Hourglass, Download, Loader2, Home,
  FileText, FileSpreadsheet, FileType, ChevronDown, MapPin,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const PERIODS = [
  { key: "day", label: "Día" },
  { key: "month", label: "Mes" },
  { key: "year", label: "Año" },
];
const PIE_COLORS = ["#003366", "#7C3AED", "#F59E0B", "#10B981", "#EF4444"];
const ABS_LABEL = { vacation: "Vacaciones", sick: "Baja médica", personal: "Asuntos propios", other: "Otros" };

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-nav rounded-lg border border-slate-200 px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold text-slate-700 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-slate-600">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}h</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("month");
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [empFilter, setEmpFilter] = useState("");
  const [depFilter, setDepFilter] = useState("");
  const [locations, setLocations] = useState([]);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([
      api.get(`/dashboard/stats?period=${period}`),
      api.get(`/dashboard/charts?period=${period}`),
    ]);
    setStats(s.data);
    setCharts(c.data);
  }, [period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/doc-employees").then(({ data }) => setEmployees(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/dashboard/team-locations").then(({ data }) => setLocations(data.points || [])).catch(() => {}); }, []);

  const EXT = { csv: "csv", xlsx: "xlsx", pdf: "pdf" };
  const exportReport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ period, format });
      if (empFilter) params.set("employee_id", empFilter);
      else if (depFilter) params.set("department_id", depFilter);
      const res = await api.get(`/reports/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `informe_fichaje_${period}.${EXT[format]}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Informe ${format.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Métricas de control horario en tiempo real.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-white border border-slate-200 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button key={p.key} data-testid={`period-${p.key}`} onClick={() => setPeriod(p.key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${period === p.key ? "text-white" : "text-slate-500 hover:text-slate-800"}`}
                style={period === p.key ? { background: "var(--brand)" } : {}}>
                {p.label}
              </button>
            ))}
          </div>
          <Select value={empFilter || "all"} onValueChange={(v) => { setEmpFilter(v === "all" ? "" : v); setDepFilter(""); }}>
            <SelectTrigger className="w-44 bg-white" data-testid="report-employee-filter"><SelectValue placeholder="Empleado" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos los empleados</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={depFilter || "all"} onValueChange={(v) => { setDepFilter(v === "all" ? "" : v); setEmpFilter(""); }}>
            <SelectTrigger className="w-44 bg-white" data-testid="report-dept-filter"><SelectValue placeholder="Departamento" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos los departamentos</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={exporting} data-testid="export-report-btn" className="brand-btn">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Exportar <ChevronDown className="h-4 w-4 ml-1" /></>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportReport("pdf")} data-testid="export-pdf"><FileType className="h-4 w-4 mr-2 text-rose-500" /> PDF con marca</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("xlsx")} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (XLSX)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport("csv")} data-testid="export-csv"><FileText className="h-4 w-4 mr-2 text-slate-500" /> CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!stats ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testId="kpi-worked" label="Horas trabajadas" value={stats.worked_hours} suffix="h" icon={Clock} accent />
            <KpiCard testId="kpi-overtime" label="Horas extra" value={stats.overtime_hours} suffix="h" icon={Timer} />
            <KpiCard testId="kpi-present" label="Fichando ahora" value={stats.present_now} icon={UserCheck} />
            <KpiCard testId="kpi-employees" label="Empleados" value={stats.total_employees} icon={Users} />
            <KpiCard testId="kpi-absences" label="Ausencias aprobadas" value={stats.approved_absences} icon={CalendarOff} />
            <KpiCard testId="kpi-pending" label="Solicitudes pendientes" value={stats.pending_requests} icon={Hourglass} />
            <KpiCard testId="kpi-remote" label="Fichajes en teletrabajo" value={stats.remote_shifts ?? 0} icon={Home} />
            <KpiCard testId="kpi-avg" label="Media por empleado" value={stats.avg_hours_per_employee} suffix="h" icon={Clock} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
              <h3 className="font-heading text-lg font-semibold mb-1">Evolución de horas</h3>
              <p className="text-xs text-slate-400 mb-4">Trabajadas vs. extra por periodo</p>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={charts?.timeseries || []}>
                  <defs>
                    <linearGradient id="gW" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gO" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="worked" name="Trabajadas" stroke="var(--brand)" strokeWidth={2.5} fill="url(#gW)" />
                  <Area type="monotone" dataKey="overtime" name="Extra" stroke="#F59E0B" strokeWidth={2} fill="url(#gO)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border-slate-200 shadow-none">
              <h3 className="font-heading text-lg font-semibold mb-1">Ausencias</h3>
              <p className="text-xs text-slate-400 mb-4">Por tipo</p>
              {charts?.absences?.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={charts.absences.map(a => ({ ...a, name: ABS_LABEL[a.name] || a.name }))}
                      dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                      {charts.absences.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">Sin datos</div>}
              <div className="space-y-1.5 mt-2">
                {(charts?.absences || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {ABS_LABEL[a.name] || a.name}
                    </span>
                    <span className="font-semibold">{a.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-slate-200 shadow-none">
              <h3 className="font-heading text-lg font-semibold mb-4">Horas por departamento</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={charts?.by_department || []} layout="vertical" barCategoryGap={12}>
                  <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={110} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="worked" name="Trabajadas" fill="var(--brand)" radius={[0, 6, 6, 0]} />
                  <Bar dataKey="overtime" name="Extra" fill="#F59E0B" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border-slate-200 shadow-none">
              <h3 className="font-heading text-lg font-semibold mb-4">Top empleados por horas</h3>
              <div className="space-y-3">
                {(charts?.by_employee || []).map((e, i) => {
                  const max = charts.by_employee[0]?.worked || 1;
                  return (
                    <div key={i} data-testid={`emp-bar-${i}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700">{e.name}</span>
                        <span className="text-slate-500 tabular-nums">{e.worked}h {e.overtime > 0 && <span className="text-amber-500">(+{e.overtime}h)</span>}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(e.worked / max) * 100}%`, background: "var(--brand)" }} />
                      </div>
                    </div>
                  );
                })}
                {!charts?.by_employee?.length && <div className="text-sm text-slate-400 py-8 text-center">Sin datos en este periodo</div>}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><MapPin className="h-5 w-5 text-sky-500" /> Mapa de fichajes del equipo</h3>
                <p className="text-xs text-slate-400 mt-1">Último fichaje con ubicación de cada empleado. <span className="inline-flex items-center gap-1 ml-1"><span className="h-2 w-2 rounded-full bg-sky-500" /> Presencial</span> <span className="inline-flex items-center gap-1 ml-2"><span className="h-2 w-2 rounded-full bg-violet-500" /> Teletrabajo</span></p>
              </div>
              <span className="text-sm text-slate-500" data-testid="map-points-count">{locations.length} ubicación(es)</span>
            </div>
            {locations.length ? (
              <TeamMap points={locations} />
            ) : (
              <div className="h-[200px] flex flex-col items-center justify-center text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl" data-testid="map-empty">
                <MapPin className="h-8 w-8 mb-2 text-slate-300" />
                Aún no hay fichajes con ubicación.<br />Activa la geolocalización en <b>Marca Blanca</b> para ver el mapa.
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
